import { Suspense, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import {
  animate,
  AnimatePresence,
  motion,
  useIsPresent,
  useMotionValue,
  useReducedMotion,
  useTransform,
  type Variants,
} from 'motion/react';
import { useStore } from '../../lib/store';
import { player } from '../../player/engine';
import { useDesktopLayout } from '../theme/layout';
import { curves, seconds, transitions } from '../theme/motion';
import { overlayDestination, type OverlayId, type TabId } from './destinations';
import { TakeFocus, useTakeFocusOnOpen } from './focus';
import { LayerErrorBoundary } from './LayerErrorBoundary';
import { nav, rememberTabLocation, resetTabMemory } from './nav';
import type { NavDirection, NavLocation } from './navigator';
import { coverDelayMs, layerVariants, pageExit, type LayerMotion } from './pageMotion';
import { PaneActiveContext, SidebarDestinationContext } from './pane';
import { tabOfBase, type BaseRouteClass } from './routes';
import { MiniPlayer, OVERLAY_SCREENS, PaneRoutes } from './screens';
import { scrollPaneToTop } from './scrollToTop';
import { SlidingNavBar } from './SlidingNavBar';
import { TabStack } from './TabStack';
import { useDelayedFlag } from './useDelayedFlag';
import styles from './ShellLayer.module.css';

export interface ShellLayerProps {
  /** The tab or overlay location the shell shows. */
  base: NavLocation;
  baseRoute: BaseRouteClass;
  /** Nothing is layered above the shell. */
  interactive: boolean;
  /** A pushed page fully covers the shell (after its transition). */
  obscured: boolean;
}

const MENU_ROOT: NavLocation = { pathname: '/menu', search: '', hash: '', state: null, key: 'menu-root' };

type TabLocations = Partial<Record<TabId, NavLocation>>;

function nextTabLocations(prev: TabLocations, route: BaseRouteClass, base: NavLocation): TabLocations {
  const tab = tabOfBase(route);
  if (route.kind === 'tab') return prev[tab]?.key === base.key && prev[tab]?.search === base.search ? prev : { ...prev, [tab]: base };
  return prev.menu ? prev : { ...prev, menu: MENU_ROOT };
}

interface OverlayMotion extends LayerMotion {
  /** Closed onto another tab: the overlay leaves the way the Menu pane would, with the tab slide. */
  toTab: boolean;
}

/** A failed overlay goes back like its screen's Back button (phones; on desktop the sidebar is the way out). */
const OVERLAY_EXIT = { kind: 'back', onLeave: () => nav.back() } as const;

/**
 * Overlays push and pop like pages (polish pass): the Cupertino transition
 * over the tab stack, which shifts by a third underneath. Closing onto another
 * tab, the overlay slides out with the tab-switch slide as that tab comes in.
 */
const overlayVariants: Variants = {
  ...layerVariants,
  exit: (motionState: OverlayMotion) =>
    motionState.toTab && !motionState.reduced
      ? { x: '100%', transition: transitions.tabSlide }
      : pageExit(motionState.toTab ? { ...motionState, direction: 'back' } : motionState),
};

/**
 * Desktop layout: overlays are sidebar destinations, so they switch at once
 * like the tabs do.
 */
const desktopOverlayVariants: Variants = {
  enter: { x: 0, opacity: 1 },
  active: { x: 0, opacity: 1, transition: { duration: 0 } },
  exit: { opacity: 0, transition: { duration: 0 } },
};

/** The tab stack under an overlay: parallax like the route below a page; instantly back in place for a tab switch. */
const stackVariants: Variants = {
  ...layerVariants,
  reset: { x: '0%', opacity: 1, transition: { duration: 0 } },
};

/** Which way the overlay last changed, for its transition and the stack's. */
function useOverlayMotion(overlay: OverlayId | null, activeTab: TabId, reduced: boolean): OverlayMotion {
  const [seen, setSeen] = useState<{ overlay: OverlayId | null; direction: NavDirection; toTab: boolean }>({
    overlay,
    direction: 'none',
    toTab: false,
  });
  if (seen.overlay !== overlay) {
    const next = { overlay, direction: (overlay ? 'forward' : 'back') as NavDirection, toTab: overlay === null && activeTab !== 'menu' };
    setSeen(next);
    return { direction: next.direction, toTab: next.toTab, reduced };
  }
  return { direction: seen.direction, toTab: seen.toTab, reduced };
}

interface OverlayPaneProps {
  overlay: OverlayId;
  location: NavLocation;
  active: boolean;
  /** Opened by the user (not restored with the shell): takes focus from the now inert tab stack. */
  takeFocus: boolean;
  motionState: OverlayMotion;
  desktop: boolean;
}

function OverlayPane({ overlay, location, active, takeFocus, motionState, desktop }: OverlayPaneProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isPresent = useIsPresent();
  useTakeFocusOnOpen(ref, takeFocus);
  return (
    <motion.div
      ref={ref}
      className={styles.overlay}
      tabIndex={-1}
      role="main"
      aria-label={overlayDestination(overlay).label}
      data-overlay={overlay}
      custom={motionState}
      variants={desktop ? desktopOverlayVariants : overlayVariants}
      initial="enter"
      animate="active"
      exit="exit"
      // A closing overlay is inert while it slides out, so focus left in it returns to the Menu pane.
      inert={!isPresent}
    >
      <PaneActiveContext value={active && isPresent}>
        <SidebarDestinationContext value={desktop}>
          <LayerErrorBoundary exit={desktop ? undefined : OVERLAY_EXIT}>
            <Suspense fallback={<div className={styles.fallback} aria-busy="true" />}>
              <PaneRoutes location={location} Screen={OVERLAY_SCREENS[overlay]} />
              <TakeFocus layerRef={ref} enabled={takeFocus} />
            </Suspense>
          </LayerErrorBoundary>
        </SidebarDestinationContext>
      </PaneActiveContext>
    </motion.div>
  );
}

/**
 * The docked mini player (polish pass): slides up from behind the nav bar
 * when a book loads (250 ms easeOutCubic) and back down when it unloads
 * (200 ms easeInCubic); with reduced motion it fades. The bottom inset every
 * scroll view pads by (`--shell-mini-player-height`) follows the same
 * progress, so content never jumps.
 */
function MiniPlayerDock({ shown, shellRef }: { shown: boolean; shellRef: RefObject<HTMLDivElement | null> }) {
  const reduced = useReducedMotion() ?? false;
  const reveal = useMotionValue(shown ? 1 : 0);
  const y = useTransform(reveal, [0, 1], ['100%', '0%']);
  const [mounted, setMounted] = useState(shown);
  if (shown && !mounted) setMounted(true);
  // Clipped only while sliding: a clip-path makes the dock a backdrop root,
  // which would leave the mini player's backdrop blur nothing to blur at rest.
  const [sliding, setSliding] = useState(false);

  useLayoutEffect(() => {
    const shell = shellRef.current;
    if (!shell) return;
    const apply = (progress: number) => shell.style.setProperty('--shell-mini-player-height', `calc(var(--mini-player-height) * ${progress})`);
    apply(reveal.get());
    return reveal.on('change', apply);
  }, [reveal, shellRef]);

  useEffect(() => {
    const target = shown ? 1 : 0;
    if (reveal.get() === target) {
      if (!shown) setMounted(false);
      return;
    }
    setSliding(true);
    const controls = animate(reveal, target, {
      duration: reduced ? seconds.quick : shown ? seconds.sheetEnter : seconds.sheetExit,
      ease: shown ? curves.easeOutCubic : curves.easeInCubic,
      onComplete: () => {
        setSliding(false);
        if (!shown) setMounted(false);
      },
    });
    return () => controls.stop();
  }, [shown, reduced, reveal]);

  if (!mounted) return null;
  return (
    <div className={styles.dock} data-sliding={sliding || undefined}>
      <motion.div className={styles.dockSlide} style={reduced ? { opacity: reveal } : { y }}>
        <LayerErrorBoundary>
          <Suspense fallback={null}>
            <MiniPlayer />
          </Suspense>
        </LayerErrorBoundary>
      </motion.div>
    </div>
  );
}

/**
 * The signed-in shell. The caller keys it by user scope + library, so a
 * connection or library switch remounts every tab (SHELL-004). Overlays push
 * over the tab stack like pages; the stack stays mounted underneath.
 *
 * Desktop layout: the navigator draws the sidebar and the now-playing bar
 * instead of the nav bar and the mini player, and tabs and overlays switch
 * at once.
 */
export function ShellLayer({ base, baseRoute, interactive, obscured }: ShellLayerProps) {
  const activeTab = tabOfBase(baseRoute);
  const overlay = baseRoute.kind === 'overlay' ? baseRoute.overlay : null;
  const hasMiniPlayer = useStore(player.store, (s) => s.itemId !== null);
  const reduced = useReducedMotion() ?? false;
  const desktop = useDesktopLayout();
  const overlayMotion = { ...useOverlayMotion(overlay, activeTab, reduced), desktop };
  // The tab stack keeps painting while an overlay slides over it (on desktop, under the opaque overlay until the page fade's length).
  const stackCovered = useDelayedFlag(overlay !== null, coverDelayMs(reduced, desktop));
  const shellRef = useRef<HTMLDivElement>(null);

  const [initialBaseKey] = useState(base.key);
  const [tabLocations, setTabLocations] = useState<TabLocations>(() => nextTabLocations({}, baseRoute, base));
  const updated = nextTabLocations(tabLocations, baseRoute, base);
  if (updated !== tabLocations) setTabLocations(updated);

  useEffect(() => {
    if (baseRoute.kind === 'tab') rememberTabLocation(baseRoute.tab, base);
  }, [base, baseRoute]);

  useEffect(() => resetTabMemory, []);

  // Re-tapping the shown tab scrolls its screen back to the top (deviation 22);
  // with an overlay open, tapping Menu closes it (nav.tab), back to the tab's root.
  const selectTab = (tab: TabId) => {
    if (tab === activeTab && overlay === null && interactive) {
      const pane = shellRef.current?.querySelector<HTMLElement>(`[data-tab="${tab}"]`);
      if (pane) scrollPaneToTop(pane, reduced);
      return;
    }
    nav.tab(tab);
  };

  return (
    <div ref={shellRef} className={[styles.shell, obscured ? styles.obscured : ''].filter(Boolean).join(' ')} data-desktop={desktop || undefined}>
      <motion.div
        className={styles.stackLayer}
        custom={overlayMotion}
        variants={stackVariants}
        initial={false}
        animate={overlay ? 'covered' : overlayMotion.toTab ? 'reset' : 'active'}
      >
        <TabStack
          activeTab={activeTab}
          locations={updated}
          hidden={overlay !== null || obscured}
          visible={!stackCovered && !obscured}
          interactive={interactive && overlay === null}
          instant={desktop}
        />
      </motion.div>
      <AnimatePresence initial={false} custom={overlayMotion}>
        {overlay && (
          <OverlayPane
            key={overlay}
            overlay={overlay}
            location={base}
            active={interactive}
            takeFocus={base.key !== initialBaseKey}
            motionState={overlayMotion}
            desktop={desktop}
          />
        )}
      </AnimatePresence>
      {!desktop && <MiniPlayerDock shown={hasMiniPlayer} shellRef={shellRef} />}
      {!desktop && <SlidingNavBar activeTab={activeTab} onSelect={selectTab} />}
    </div>
  );
}
